// SETTINGS V2 — Notifikácie (Notification Preferences) page (SETTINGS_V2_SP5).
//
// Routed real page (route swap on useCustomerRoutes line 318). Binds the reused
// notificationPreferencesSlice + customerSettingsSlice UNCHANGED (§2). Auto-save,
// NO Save button: every toggle dispatches a WHOLE-ARRAY PUT via toUpdatePayload
// (NO id, USER-scoped). Toggle state is fulfilled-applied, NOT optimistic — the
// slice flips state.preferences only on updateNotificationPreferences.fulfilled,
// so an in-flight switch shows the OLD value and a rejected PUT leaves it
// un-flipped. v2 ADDS the §7 failure callout (a failed save was silent before) +
// the §2 silent in-flight guard (closes the whole-array stale-payload race).
// Inline styles + scoped CSS-var tokens + v2 atoms; responsive via useBreakpoint.
// No MUI, no sx, no dt tokens, no Tailwind. Page ROOT carries NO animation (SP2).
import React, { useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import Loader from "../../../../shared/components/Loader";
import {
  NotificationPreference,
  UpdateNotificationPreference,
} from "../../../../shared/notifications/models/notificationModels";
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from "../../../../shared/notifications/notificationPreferencesSlice";
import { CustomerTypeCode } from "../../../shared/models/customerModels";
import { getCustomer } from "../../customerSettingsSlice";
import { SErrorCallout } from "../atoms";
import {
  NOTIF_CATEGORIES,
  NotifCategoryDef,
  toUpdatePayload,
} from "./notificationsV2Data";
import NotificationCategoryCardV2 from "./NotificationCategoryCardV2";

// ONE page-local error union (§7). forbidden → title-only, NO retry (SEC-REQ-6).
type PageError =
  | { kind: "load" | "save"; message?: string; retry: () => void }
  | { kind: "forbidden" };

// RolePill — parity semantics (owner / provider / both), designer visuals. Both
// pill hexes are category-identity constants (sanctioned literals, §5.2).
const PILL_BASE: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 600,
  letterSpacing: "0.07em", textTransform: "uppercase", padding: "3px 9px", borderRadius: 999,
};
const RolePill: React.FC<{ customerType?: number; t: (k: string) => string }> = ({ customerType, t }) => {
  if (!customerType) return null;
  const isOwner = customerType === CustomerTypeCode.MachineOperator || customerType === CustomerTypeCode.Both;
  const isProvider = customerType === CustomerTypeCode.ServiceProvider || customerType === CustomerTypeCode.Both;
  return (
    <span style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
      {isOwner && (
        <span style={{ ...PILL_BASE, color: "#D4572B", background: "rgba(212,87,43,0.10)", border: "1px solid rgba(212,87,43,0.28)" }}>
          {t("settings:notifications.roleMachineOwner")}
        </span>
      )}
      {isProvider && (
        <span style={{ ...PILL_BASE, color: "#3B82F6", background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.28)" }}>
          {t("settings:notifications.roleServiceProvider")}
        </span>
      )}
    </span>
  );
};

// Worker banner (§5.3) — info strip, scoped tokens, inline ℹ glyph.
const WorkerBanner: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 14px", borderRadius: 10,
      background: "var(--bg-soft)", border: "1px solid var(--border)",
      animation: "sv2-card-reveal 0.4s ease-out both",
    }}
  >
    <span aria-hidden="true" style={{ color: "var(--mod-accent)", fontSize: 15, lineHeight: 1.4, flexShrink: 0 }}>ℹ</span>
    <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>{text}</div>
  </div>
);

const NotificationsPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");

  const preferences = useAppSelector((s) => s.notificationPreferences.preferences);
  const loading = useAppSelector((s) => s.notificationPreferences.loading);
  const actionLoading = useAppSelector((s) => s.notificationPreferences.actionLoading);
  const ownCustomer = useAppSelector((s) => s.customerSettings.customer);

  const [pageError, setPageError] = useState<PageError | null>(null);

  // Fetch on mount, rejected-captured (§7 rows 1-2).
  async function runFetch() {
    const r = await dispatch(fetchNotificationPreferences());
    if (fetchNotificationPreferences.rejected.match(r)) {
      setPageError({ kind: "load", message: r.error.message, retry: runFetch });
    } else {
      setPageError(null);
    }
  }

  // Mount effect parity (legacy 497-502): prefs ALWAYS; getCustomer only when
  // !ownCustomer (visiting Company first legitimately skips it — same today).
  useEffect(() => {
    runFetch();
    if (!ownCustomer) dispatch(getCustomer());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group prefs by category (port verbatim, legacy 505-524): filter to a bucket's
  // eventTypes, keep non-empty, sort by the category's array order.
  const grouped = useMemo(() => {
    const map = new Map<string, NotificationPreference[]>();
    for (const def of NOTIF_CATEGORIES) {
      const bucket = preferences.filter((p) => def.eventTypes.includes(p.eventType));
      if (bucket.length > 0) {
        map.set(
          def.key,
          [...bucket].sort(
            (a, b) => def.eventTypes.indexOf(a.eventType) - def.eventTypes.indexOf(b.eventType)
          )
        );
      }
    }
    return map;
  }, [preferences]);

  const activeCategories = useMemo(
    () => NOTIF_CATEGORIES.filter((c) => grouped.has(c.key)),
    [grouped]
  );

  // Worker heuristic (§2) — only the Operations bucket came back.
  const isWorkerOnly =
    activeCategories.length === 1 && activeCategories[0]?.key === "operations";

  // Whole-array PUT + fulfilled/rejected capture (§6). `updated` is captured in the
  // closure so retry re-dispatches the SAME absolute-value payload.
  async function runSave(updated: UpdateNotificationPreference[]) {
    const result = await dispatch(updateNotificationPreferences(updated));
    if (updateNotificationPreferences.fulfilled.match(result)) {
      setPageError(null);
      return;
    }
    setPageError(
      result.error.code === "403"
        ? { kind: "forbidden" }
        : { kind: "save", message: result.error.message, retry: () => runSave(updated) }
    );
  }

  // Per-event toggle (§6.1). §2 silent in-flight guard first.
  const handleTogglePref = (eventType: number, field: "isInAppEnabled" | "isEmailEnabled") => {
    if (actionLoading) return;
    const updated: UpdateNotificationPreference[] = preferences.map((p) => {
      const base = toUpdatePayload(p);
      if (p.eventType !== eventType) return base;
      return { ...base, [field]: !p[field] };
    });
    runSave(updated);
  };

  // Category "Všetko" (§6.2) — flips In-App ONLY; Email pass-through untouched.
  const handleToggleCategoryAll = (def: NotifCategoryDef, items: NotificationPreference[]) => {
    if (actionLoading) return;
    const anyOn = items.some((i) => i.isInAppEnabled);
    const target = !anyOn; // any on → all off; all off → all on
    const eventTypesInCat = new Set(items.map((i) => i.eventType));
    const updated: UpdateNotificationPreference[] = preferences.map((p) => {
      const base = toUpdatePayload(p);
      if (!eventTypesInCat.has(p.eventType)) return base;
      return { ...base, isInAppEnabled: target };
    });
    runSave(updated);
  };

  const pageErrorTitle =
    pageError?.kind === "forbidden" ? t("settings:notifications.permissionDenied")
      : pageError?.kind === "load" ? t("settings:notifications.loadFailed")
        : pageError?.kind === "save" ? t("settings:notifications.saveFailed") : "";
  const pageErrorBody =
    pageError && pageError.kind !== "forbidden"
      ? pageError.message || t("settings:notifications.genericError")
      : undefined;

  const renderCards = () => {
    // Loading (parity): centered Loader replacing the card area.
    if (loading && preferences.length === 0) {
      return (
        <div style={{ display: "grid", placeItems: "center", padding: "48px 0" }}>
          <Loader />
        </div>
      );
    }
    // Fetch-failed empty → callout above is the only content; empty (no error) →
    // blank (parity — unreachable for roles that can open the page).
    if (preferences.length === 0) return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {activeCategories.map((def, idx) => (
          <NotificationCategoryCardV2
            key={def.key}
            def={def}
            items={grouped.get(def.key)!}
            index={idx}
            busy={actionLoading}
            onTogglePref={handleTogglePref}
            onToggleCategoryAll={handleToggleCategoryAll}
          />
        ))}
      </div>
    );
  };

  return (
    // Page root: NO animation/transform (SP2 §2 rule).
    <div style={{ display: "flex", flexDirection: "column", gap: 11, minWidth: 0, flex: 1 }}>
      {/* Subtitle row + RolePill (designer PageNotifikacie). */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--muted)", flex: 1, minWidth: 0 }}>
          {t("settings:notifications.description")}
        </div>
        <RolePill customerType={ownCustomer?.customerType} t={t} />
      </div>

      {isWorkerOnly && <WorkerBanner text={t("settings:notifications.workerBanner")} />}

      {pageError && (
        <SErrorCallout
          title={pageErrorTitle}
          body={pageErrorBody}
          actionLabel={pageError.kind !== "forbidden" ? t("settings:notifications.retry") : undefined}
          onAction={pageError.kind !== "forbidden" ? pageError.retry : undefined}
        />
      )}

      {renderCards()}

      {preferences.length > 0 && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)", letterSpacing: "0.04em" }}>
          {t("settings:notifications.footnote")}
        </div>
      )}
    </div>
  );
};

export default NotificationsPageV2;
